import * as React from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * When true and the current value is non-empty, renders a small X
   * button at the right end of the input. Clicking the X (or pressing
   * Enter/Space while it is focused) calls `onChange` with an empty
   * string and re-focuses the underlying input. Pressing Escape inside
   * the input clears it too — the conventional search-box shortcut.
   *
   * Purely additive: existing call sites that don't pass `clearable`
   * are bit-for-bit unchanged (no wrapper, no extra DOM). #1213.
   */
  clearable?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, clearable, value, onChange, onKeyDown, ...props }, ref) => {
    // Local ref so the X button can re-focus the input after clearing.
    // We still forward the ref via callback so external refs keep working.
    const innerRef = React.useRef<HTMLInputElement | null>(null)
    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node
      },
      [ref],
    )

    // Build a synthetic ChangeEvent so consumers that read
    // `e.target.value` (the common shape) keep working. We can't
    // construct a real React.ChangeEvent without React internals, so
    // we cast a minimal stand-in — the same trick the testing-library
    // ecosystem uses.
    const emitClear = React.useCallback(() => {
      if (!onChange || !innerRef.current) return
      const node = innerRef.current
      // Use the native setter so the browser fires a real input event
      // for non-React consumers; React's synthetic onChange below still
      // gets the empty value via the constructed event.
      const setter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      )?.set
      setter?.call(node, '')
      const event = {
        target: node,
        currentTarget: node,
        type: 'change',
        bubbles: true,
        cancelable: false,
      } as unknown as React.ChangeEvent<HTMLInputElement>
      onChange(event)
      node.focus()
    }, [onChange])

    const hasValue =
      clearable && value !== undefined && value !== null && String(value).length > 0

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Escape-with-content clears the input. Escape-empty falls
      // through so a parent modal/dialog can still close on Escape —
      // matches the search-box pattern in VSCode, Slack, GitHub.
      if (clearable && e.key === 'Escape' && hasValue) {
        e.preventDefault()
        e.stopPropagation()
        emitClear()
        return
      }
      onKeyDown?.(e)
    }

    const handleClearKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        emitClear()
      }
    }

    // Shared classes for the underlying <input>.
    const inputClass = cn(
      'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
      // Reserve space on the right for the X so long values don't
      // collide with the icon. We add the padding unconditionally when
      // `clearable` is set — the button only renders when non-empty,
      // but the alternative of conditionally padding causes the caret
      // to jump as the user types the first/last character.
      clearable && 'pr-8',
      className,
    )

    // Fast path: no clearable, no wrapper. Existing call sites stay
    // bit-for-bit unchanged.
    if (!clearable) {
      return (
        <input
          type={type}
          className={cn(
            'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          ref={ref}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          {...props}
        />
      )
    }

    return (
      <div className="relative">
        <input
          type={type}
          className={inputClass}
          ref={setRefs}
          value={value}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          {...props}
        />
        {hasValue && (
          <button
            type="button"
            aria-label="Clear"
            onClick={emitClear}
            onKeyDown={handleClearKeyDown}
            // `mousedown` would steal focus from the input before the
            // click fires; preventing default keeps focus on the input
            // until our handler runs and re-focuses it explicitly.
            onMouseDown={(e) => e.preventDefault()}
            tabIndex={0}
            className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-5 w-5 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    )
  },
)
Input.displayName = 'Input'

export { Input }
